<template>
  <div class="guideline-setting">
    <div class="guideline-header">
      <div class="guideline-header__information">
        <span v-if="lastUpdatedBy">{{ $t('lastUpdatedBy',[lastUpdatedBy, lastUpdatedAt]) }}</span>
      </div>
      <div
        v-if="aclHelper.getSettingsUpdateAccess()"
        class="guideline-header__btns"
      >
        <button
          v-if="!isEditing && guidelines.length"
          role="publish-button"
          class="btn btn--darkblue"
          @click="() => openPublishModal()"
        >
          {{ $t('action.publish') }}
        </button>
        <button
          v-if="!isEditing"
          role="edit-button"
          class="btn btn--orange"
          @click="() => enableEdit()"
        >
          {{ $t('action.edit') }}
        </button>
        <button
          v-if="isEditing"
          role="cancel-button"
          class="btn btn--darkblue"
          @click="() => cancelEdit()"
        >
          {{ $t('action.cancel') }}
        </button>
        <button
          v-if="isEditing"
          role="save-button"
          class="btn btn--orange"
          @click="() => save()"
        >
          {{ $t('action.save') }}
        </button>
      </div>
    </div>
    <section
      v-if="guidelines.length"
      class="guideline"
    >
      <Collapse
        v-for="(section, sectionIndex) in guidelines"
        :id="`guideline-setting-collapse-${sectionIndex}`"
        :key="`${sectionIndex}-${section.displayOrder}-${section.value}`"
        :withBorder="false"
        :titleContainerClass="{'is-editing': isEditing || onlyLine}"
        class="guideline-setting-collapse"
        :expandIcons="onlyLine ? [] : undefined"
      >
        <template v-if="!onlyLine">
        <div
          slot="title"
          :class="['guideline-section', `section_group_${sectionIndex}`, `${section.class}`]"
        >
          <TextField
            class="guideline-section-edit__order"
            :showColon="false"
            :value="isEditing ? section.displayOrder : displayOrder(section)"
            :readonly="!isEditing"
            :required="isEditing"
            defaultEmpty
            @updateValue="val => updateSectionDisplayOrder(val, sectionIndex)"
          />
          <TextField
            class="guideline-section__content"
            :showColon="false"
            :placeholder="$t('guideline.sectionInputHint')"
            :value="section.value"
            :readonly="!isEditing"
            :required="isEditing"
            defaultEmpty
            @updateValue="val => updateSectionContent(val, sectionIndex)"
          />
        </div>
        <div
          v-if="isEditing"
          slot="additional"
          class="additional-container"
          :class="{hidden: !isEditing}"
        >
          <DottedMenu
            v-if="isEditing"
            ref="dottedMenu_section"
            class="guideline-dropdown"
            color="darkblue"
            :enableClickOutClose="true"
          >
            <HKLbutton @click="() => addSection(sectionIndex, true)">
              {{ $t('action.addSectionAbove') }}
            </HKLbutton>

            <HKLbutton @click="() => addSection(sectionIndex, false)">
              {{ $t('action.addSectionBelow') }}
            </HKLbutton>

            <HKLbutton @click="() => deleteSection(sectionIndex)">
              {{ $t('action.deleteSection') }}
            </HKLbutton>
          </DottedMenu>
        </div>
        </template>
        <div
          :id="`guideline-setting-section-${sectionIndex}`"
          slot="content"
          class="guideline-section__line"
        >
          <div
            v-for="(line, lineIndex) in section.sectionContent"
            :key="`${lineIndex}-${line.displayOrder}-${line.value}`"
            :class="['guideline-line', `line_group_${sectionIndex}_${lineIndex}`, `${line.class}`]"
          >
            <div class="guideline-line__line">
              <TextField
                v-if="isLineOrderEditable"
                class="guideline-line-edit__order"
                :showColon="false"
                :value="isEditing ? line.displayOrder : displayOrder(line)"
                :readonly="!isEditing"
                :required="isEditing"
                defaultEmpty
                @updateValue="val => updateLineDisplayOrder(val, sectionIndex, lineIndex)"
              />
              <span
                v-else
                class="guideline-line__order"
              >
                {{ displayOrder(line, { isSub: true, plusOne: !section.sectionContent[0].order }) }}
              </span>
              <TextField
                class="guideline-line__content"
                :placeholder="$t('guideline.lineInputHint')"
                :value="line.value"
                :showColon="false"
                :readonly="!isEditing"
                :required="isEditing"
                defaultEmpty
                @updateValue="val => updateLineContent(val, sectionIndex, lineIndex)"
              />
              <DottedMenu
                v-if="isEditing"
                :ref="'dottedMenu_line_' + line.id"
                class="guideline-dropdown"
                :enableClickOutClose="true"
              >
                <HKLbutton @click="() => addLine({sectionIndex: sectionIndex, lineIndex: lineIndex, isAbove: true, id: line.id})">
                  {{ $t('action.addLineAbove') }}
                </HKLbutton>

                <HKLbutton @click="() => addLine({sectionIndex: sectionIndex, lineIndex: lineIndex, isAbove: false, id: line.id})">
                  {{ $t('action.addLineBelow') }}
                </HKLbutton>

                <HKLbutton @click="() => deleteLine(sectionIndex, lineIndex)">
                  {{ $t('action.deleteLine') }}
                </HKLbutton>
              </DottedMenu>
            </div>
          </div>
        </div>
      </Collapse>
    </section>

    <modal
      ref="cancelModal"
      :showCloseBtn="false"
      :modalId="cancelModalId"
    >
      <div slot="content">
        <div class="font-bold">
          {{ $t('cancelChanges') }}
        </div>
      </div>
      <div slot="btns">
        <button
          type="button"
          role="modal-no-button"
          class="btn--darkblue"
          @click="() => closeCancelModal()"
        >
          {{ $t('action.no') }}
        </button>
        <button
          type="button"
          role="modal-yes-button"
          class="btn--orange"
          @click="() => cancel()"
        >
          {{ $t('action.yesContinue') }}
        </button>
      </div>
    </modal>

    <modal
      ref="publishModal"
      :showCloseBtn="false"
      :modalId="publishModalId"
    >
      <div slot="content">
        <div class="font-bold">
          {{ publishHint }}
        </div>
      </div>
      <div slot="btns">
        <button
          type="button"
          role="modal-no-button"
          class="btn--darkblue"
          @click="() => closePublishModal()"
        >
          {{ $t('action.no') }}
        </button>
        <button
          type="button"
          role="modal-yes-button"
          class="btn--orange"
          @click="() => publish()"
        >
          {{ $t('action.yesContinue') }}
        </button>
      </div>
    </modal>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters } from 'vuex'

import Collapse from '@/controls/Collapse'
import DottedMenu from '@/controls/DottedMenu'
import HKLbutton from '@/controls/Button/HKLButton'
import Modal from '@/controls/Modal'
import TextField from '@/controls/TextField'
import * as guidelineUtils from '@/utils/guideline'
import dateUtil from '@/utils/dateUtil'
import numberUtils from '@/utils/numberUtils'
import { aclHelper } from '@/utils/aclHelper'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { getGuidelines, updateGuidelines, publishGuidelines } from '@/api/settings/request'
import { utils as formUtils } from '@/plugin/InputChange'
import { GUIDELINE_STATUS } from '@/utils/locale'
import { GUIDELINE_TYPE } from '@/modules/settings/engineers/consts'
import { reactive } from '@vue/composition-api'
import i18n from '@/utils/i18n'

export default {
  name: 'HkldLetter',
  components: {
    Collapse,
    Modal,
    TextField,
    DottedMenu,
    HKLbutton,
  },
  props: {
    type: {
      type: String,
      required: true,
      default () {
        return 'FITOUT_LETTER'
      },
    },
    isLineOrderEditable: {
      type: Boolean,
      default: false,
    },
    validateDuplicate: {
      type: Function,
      default: () => false,
    },
    resetDuplicate: {
      type: Function,
      default: () => {},
    },
    publishHint: {
      type: String,
      default () {
        return i18n.t('guideline.publishFolder')
      },
    },
    onlyLine: {
      type: Boolean,
      default: () => false,
    },
  },
  setup () {
    return {
      displayOrder: guidelineUtils.displayOrder,
      GUIDELINE_TYPE,
      guidelines: reactive([]),
    }
  },
  data () {
    const cancelModalId = `cancelModal-${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`
    const publishModalId = `publishModal-${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`
    return {
      cancelModalId,
      publishModalId,
      isEditing: false,
      formUtils,
      backUpGuidelines: [],
      lastUpdatedAt: null,
      lastUpdatedBy: {},
      version: 1,
      numberUtils,
      deleteItemIds: [],
      dateUtil,
      aclHelper,
      isNewItemAdded: false,
    }
  },
  mounted () {
    this.getGuidelineData()
  },
  computed: {
    ...mapGetters('form', ['isPageDirty']),
  },
  methods: {
    async getGuidelineData () {
      const data = await getGuidelines(GUIDELINE_STATUS.DRAFT, this.type)
      const guidelineData = lodash.orderBy(data.guidelines, 'order')
      guidelineData.forEach(item => {
        item.class = undefined
        item.sectionContent = lodash.orderBy(item.sectionContent, 'order')
          .map((item) => {
            item.class = undefined
            return item.displayOrder ? item : Object.assign(item, { displayOrder: undefined })
          })
      })
      this.guidelines = guidelineData
      this.lastUpdatedAt = dateUtil.formatDate(lodash.get(data, 'latestPublished.updatedAt'), true)
      this.lastUpdatedBy = lodash.get(data, 'latestPublished.updator.displayName')
      this.version = data.version || 0
    },
    updateSectionDisplayOrder (val, sectionIndex) {
      this.guidelines[sectionIndex].displayOrder = val
    },
    updateLineDisplayOrder (val, sectionIndex, lineIndex) {
      this.guidelines[sectionIndex].sectionContent[lineIndex].displayOrder = val
    },
    updateSectionContent (val, sectionIndex) {
      this.guidelines[sectionIndex].value = val
    },
    updateLineContent (val, sectionIndex, lineIndex) {
      this.guidelines[sectionIndex].sectionContent[lineIndex].value = val
    },
    addSection (sectionIndex, isAbove = true) {
      const seperateIndex = isAbove ? sectionIndex : sectionIndex + 1
      const newSection = {
        value: '',
        status: GUIDELINE_STATUS.DRAFT,
        order: seperateIndex,
        version: this.version,
        sectionContent: [{
          order: 0,
          value: '',
          type: this.type,
        }],
        type: this.type,
      }
      this.guidelines = guidelineUtils.splice(this.guidelines, seperateIndex, newSection)
      this.isNewItemAdded = true
      this.closeAllDottedMenu()
    },
    deleteSection (sectionIndex) {
      if (this.guidelines[sectionIndex].id) {
        this.deleteItemIds.push(this.guidelines[sectionIndex].id)
      }

      this.guidelines = guidelineUtils.splice(this.guidelines, sectionIndex, 1)
      if (this.guidelines.length === 0) {
        const newSection = {
          value: '',
          status: GUIDELINE_STATUS.DRAFT,
          order: 0,
          version: this.version,
          sectionContent: [{
            order: 0,
            value: '',
            type: this.type,
          }],
          type: this.type,
        }
        this.guidelines.push(newSection)
      }
      this.closeAllDottedMenu()
    },
    addLine ({ sectionIndex, lineIndex, isAbove = true, id }) {
      const seperateIndex = isAbove ? lineIndex : lineIndex + 1
      const section = this.guidelines[sectionIndex]

      const newLine = {
        order: (section.sectionContent[seperateIndex - 1] || { order: -1 }).order + 1,
        value: '',
        type: this.type,
      }
      section.sectionContent = guidelineUtils.splice(section.sectionContent, seperateIndex, newLine)
      this.isNewItemAdded = true
      this.closeAllDottedMenu()
    },
    deleteLine (sectionIndex, lineIndex) {
      const section = this.guidelines[sectionIndex]

      if (section.sectionContent[lineIndex].id) {
        this.deleteItemIds.push(section.sectionContent[lineIndex].id)
      }

      section.sectionContent = guidelineUtils.splice(section.sectionContent, lineIndex, 1)

      if (section.sectionContent.length === 0) {
        const newLine = {
          order: 0,
          value: '',
          type: this.type,
        }
        section.sectionContent.push(newLine)
      }
      this.closeAllDottedMenu()
    },
    resetEdit () {
      this.isEditing = false
      formUtils.resetDirty()
      this.isNewItemAdded = false
    },
    cancelEdit () {
      if (this.isPageDirty || this.deleteItemIds.length > 0 || this.isNewItemAdded) {
        this.$refs['cancelModal'].open()
      } else {
        this.isEditing = false
      }
    },
    closeCancelModal () {
      this.$refs['cancelModal'].close()
    },
    cancel () {
      this.guidelines = lodash.cloneDeep(this.backUpGuidelines)
      this.deleteItemIds = []
      this.resetDuplicate(this.guidelines)
      this.closeCancelModal()
      this.resetEdit()
    },
    async save () {
      if (!this.validate()) return
      await updateGuidelines({ guidelines: this.guidelines, deleteItemIds: this.deleteItemIds, type: this.type })

      this.getGuidelineData()
      this.resetEdit()
    },
    validate () {
      if (typeof this.validateDuplicate === 'function') {
        this.resetDuplicate(this.guidelines)
        if (this.validateDuplicate(this.guidelines)) return
      }
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'none'}, '.guideline-setting')
      return fusionInputCheckUtil.isAllFieldsValid('.guideline-setting')
    },
    enableEdit () {
      this.backUpGuidelines = lodash.cloneDeep(this.guidelines)
      this.isEditing = true
      this.guidelines.length === 0 && this.addSection(0)
    },
    async publish () {
      await publishGuidelines({ type: this.type })
      this.getGuidelineData()
      this.closePublishModal()
    },
    openPublishModal () {
      this.$refs['publishModal'].open()
    },
    closePublishModal () {
      this.$refs['publishModal'].close()
    },
    closeAllDottedMenu () {
      const keywords = ['dottedMenu_section', 'dottedMenu_line_']
      Object.keys(this.$refs).forEach((key) => {
        let k
        for (const keyword of keywords) {
          if (key.includes(keyword)) {
            k = key
            break
          }
        }
        if (k) {
          this.$refs[k].forEach((r) => r.closeMenu())
        }
      })
    },
  },
}
</script>

<style lang="scss" scope>
.guideline {
  padding: 10px 35px 10px 24px;
  background-color: #fafbfc;
  box-sizing: border-box;
  textarea::placeholder {
    font-weight: normal;
    font-style: italic;
  }
}

.guideline-setting-collapse {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  .additional-container {
    width: 25px;
    display: flex;
    justify-content: center;
  }
  .title-container {
    width: 100%;
    &.is-editing {
      width: calc(100% - 25px); // The 25px is for .additional-container
      position: relative;
      i.fa.angle {
        position: absolute;
        right: 10px;
        bottom: 3px;
      }
    }
  }
  .content {
    width: 100%
  }
  i.fa.angle {
    font-size: 25px;
    color: #79797a;
  }
}

.guideline-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  &__information {
    span {
      font-size: 14px;
      font-style: italic;
      font-family: Verdana;
      font-weight: normal;
      color: #878787;
      line-height: 50px;
    }
  }
}
// For extend
.common-drop-down {
  align-self: center;
  .tender-ellipsis-bg {
    background-color: white;
    li {
      border: solid 1px #878787;
    }
    li > a {
      color: #00314a;
      font-weight: normal;
      &:not(:last-child) {
        border-bottom: solid 1px #878787;
      }
    }
    li > a:hover {
      background-color: #5d7c9d;
    }
  }
}

.guideline-section {
  position: relative;
  display: flex;
  align-items: baseline;
  font-size: 14px;
  &-edit__order {
    margin-right: 5px;
    .text-input {
      width: 60px;
    }
    .text-input textarea, p {
      font-weight: bold;
    }
  }
  &__content {
    width: 100%;
    .text-input textarea {
      width: 100%;
      padding-right: 30px;
      font-weight: bold;
    }
    p {
      font-weight: bold;
      margin-bottom: 0px;
    }
  }
}
.guideline-line {
  &__line {
    display: flex;
    align-items: baseline;
  }
  &-edit__order {
    margin-right: 5px;
    .text-input {
      width: 60px;
    }
  }
  &__order {
    margin-right: 5px;
    width: 20px;
    color: #0c3a58;
  }
  &__content {
    width: 100%;

    p {
      margin-bottom: 0px;
      text-align: justify !important;
    }
    .text-input textarea {
      width: 100%;
    }
  }
}
.duplicated-group {
  .guideline-section, .guideline-line {
    &-edit__order,  &__content {
      .text-input textarea {
        background-color: #f9ecea;
      }
    }
  }
}

.guideline-dropdown {
  white-space: nowrap;
  .btn {
    min-height: unset;
  }
}
</style>
